/**
 * AnexoLibranzas Admin Page
 * Manages individual conductor days off (not group-based)
 */
import { useState, useEffect } from 'react';
import { anexoLibranzas, conductores as conductoresApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { CalendarDays, Plus, Trash2, Users, Loader2, Filter, Edit2, X } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const AVAILABLE_YEARS = [2024, 2025, 2026, 2027];

const AnexoLibranzas = () => {
    const toast = useToast();
    const [entries, setEntries] = useState([]);
    const [conductores, setConductores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [tipoFilter, setTipoFilter] = useState('bei');

    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    // Edit Modal State
    const [editModal, setEditModal] = useState({
        isOpen: false,
        date: null,
        currentConductors: [], // Array of entry objects {id, conductor_id, conductor_nombre}
        newConductorId: ''
    });

    const [filterConductor, setFilterConductor] = useState('');

    // Form state
    const [formData, setFormData] = useState({
        conductor_id: '',
        fecha: ''
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadData();
    }, [selectedYear, tipoFilter]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [entriesData, conductoresData] = await Promise.all([
                anexoLibranzas.getAll(selectedYear, tipoFilter),
                conductoresApi.getAll(null, tipoFilter)
            ]);
            setEntries(Array.isArray(entriesData) ? entriesData : []);
            setConductores(Array.isArray(conductoresData) ? conductoresData : []);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!formData.conductor_id || !formData.fecha) {
            toast.error('Selecciona conductor y fecha');
            return;
        }

        setSaving(true);
        try {
            await anexoLibranzas.create(formData);
            toast.success('Día añadido correctamente');
            setShowAddModal(false);
            setFormData({ conductor_id: '', fecha: '' });
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al añadir día');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await anexoLibranzas.delete(deleteConfirm.id);
            toast.success('Día eliminado');
            loadData();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    // Edit Logic
    const handleEditDate = (date, dateEntries) => {
        setEditModal({
            isOpen: true,
            date: date,
            currentConductors: dateEntries, // All entries for this date
            newConductorId: ''
        });
    };

    const handleRemoveFromEdit = async (entryId) => {
        try {
            await anexoLibranzas.delete(entryId);
            // Update local state immediately
            setEditModal(prev => ({
                ...prev,
                currentConductors: prev.currentConductors.filter(c => c.id !== entryId)
            }));
            // Reload global data in background
            loadData();
            toast.success('Conductor eliminado del día');
        } catch (error) {
            toast.error('Error al eliminar conductor');
        }
    };

    const handleAddToEdit = async () => {
        if (!editModal.newConductorId) return;

        try {
            // Check if already exists in local state
            if (editModal.currentConductors.some(c => c.conductor_id === parseInt(editModal.newConductorId))) {
                toast.error('Este conductor ya está añadido');
                return;
            }

            // Create in backend
            await anexoLibranzas.create({
                conductor_id: editModal.newConductorId,
                fecha: editModal.date
            });

            toast.success('Conductor añadido');
            loadData(); // Reload to get the new ID and proper object

            // For smoother UI, we'd ideally wait for reload, but we can also manually close/refresh
            setEditModal(prev => ({ ...prev, newConductorId: '' }));

            // Re-fetch only this date? Or just full reload which is fast enough usually.
            // Let's do a full reload and UPDATE editModal with new data
            // Since loadData updates 'entries', we need to sync editModal. 
            // Better strategy: Close modal? No, we want to keep editing.
            // We need to re-derive currentConductors from the fresh 'entries'.
            // For now, let's rely on loadData() updating 'entries', and we re-open logic? 
            // Actually, simplest is to just manually append a temp object if we knew the ID, 
            // but we don't know the new ID.
            // Let's await loadData() then update modal state.

            const [entriesData] = await Promise.all([anexoLibranzas.getAll(selectedYear)]);
            setEntries(entriesData);

            // Filter new entries for this date
            const newDateEntries = entriesData.filter(e => e.fecha.startsWith(editModal.date.split('T')[0]));
            setEditModal(prev => ({
                ...prev,
                currentConductors: newDateEntries,
                newConductorId: ''
            }));

        } catch (error) {
            toast.error(error.message || 'Error al añadir conductor');
        }
    };


    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-ES', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    const filteredEntries = filterConductor
        ? entries.filter(e => e.conductor_id === parseInt(filterConductor))
        : entries;

    // Group entries by date for display
    const groupedByDate = filteredEntries.reduce((acc, entry) => {
        const dateKey = entry.fecha.split('T')[0];
        if (!acc[dateKey]) {
            acc[dateKey] = [];
        }
        acc[dateKey].push(entry);
        return acc;
    }, {});

    const sortedDates = Object.keys(groupedByDate).sort();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                        <CalendarDays className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Anexo Libranzas</h1>
                        <p className="text-gray-400">Días libres individuales por conductor</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Tipo Filter */}
                    <div className="flex bg-white/5 rounded-lg p-1 border border-white/10">
                        <button
                            onClick={() => setTipoFilter('bei')}
                            className={`px-3 py-1.5 text-sm rounded-md transition-all font-semibold ${tipoFilter === 'bei' ? 'bg-cyan-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                        >
                            🚌 BEI
                        </button>
                        <button
                            onClick={() => setTipoFilter('servicio')}
                            className={`px-3 py-1.5 text-sm rounded-md transition-all font-semibold ${tipoFilter === 'servicio' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                        >
                            🚍 Servicio
                        </button>
                    </div>

                    {/* Year Selector */}
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    >
                        {AVAILABLE_YEARS.map(year => (
                            <option key={year} value={year} className="bg-[#1a1a3e]">
                                {year}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-lg hover:from-cyan-600 hover:to-purple-700 transition-all"
                    >
                        <Plus className="w-5 h-5" />
                        Añadir Día
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-gray-400 text-sm">Total Registros</p>
                    <p className="text-2xl font-bold text-white">{entries.length}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-gray-400 text-sm">Días Distintos</p>
                    <p className="text-2xl font-bold text-purple-400">{sortedDates.length}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-gray-400 text-sm">Conductores Afectados</p>
                    <p className="text-2xl font-bold text-cyan-400">
                        {new Set(entries.map(e => e.conductor_id)).size}
                    </p>
                </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                    value={filterConductor}
                    onChange={(e) => setFilterConductor(e.target.value)}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                >
                    <option value="" className="bg-[#1a1a3e]">Todos los conductores</option>
                    {conductores.map(c => (
                        <option key={c.id} value={c.id} className="bg-[#1a1a3e]">
                            {c.nombre}
                        </option>
                    ))}
                </select>
            </div>

            {/* Entries List grouped by date */}
            <div className="space-y-4">
                {sortedDates.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
                        <CalendarDays className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No hay anexo libranzas para {selectedYear}</p>
                    </div>
                ) : (
                    sortedDates.map(date => (
                        <div key={date} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                            <div className="bg-purple-500/10 px-4 py-3 border-b border-white/10 flex items-center justify-between">
                                <h3 className="text-white font-semibold flex items-center gap-2">
                                    {formatDate(date)}
                                    <span className="text-xs font-normal text-purple-300 bg-purple-500/20 px-2 py-0.5 rounded-full">
                                        {groupedByDate[date].length}
                                    </span>
                                </h3>
                                <button
                                    onClick={() => handleEditDate(date, groupedByDate[date])}
                                    className="p-1.5 hover:bg-white/10 rounded-lg text-gray-300 hover:text-white transition-colors"
                                    title="Editar grupo"
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="p-4">
                                <div className="flex flex-wrap gap-2">
                                    {groupedByDate[date].map(entry => (
                                        <div
                                            key={entry.id}
                                            className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg border border-white/10 group animate-fadeIn"
                                        >
                                            <Users className="w-4 h-4 text-purple-400" />
                                            <span className="text-white text-sm">{entry.conductor_nombre}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-[#1a1a3e] border border-white/10 rounded-xl shadow-2xl max-w-md w-full">
                        <div className="flex justify-between items-center p-6 border-b border-white/10">
                            <h2 className="text-xl font-bold text-white">Añadir Día Libre</h2> // Single entry add (original)
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleAdd} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Conductor
                                </label>
                                <select
                                    value={formData.conductor_id}
                                    onChange={(e) => setFormData({ ...formData, conductor_id: e.target.value })}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                                    required
                                >
                                    <option value="" className="bg-[#1a1a3e]">Seleccionar conductor...</option>
                                    {conductores.map(c => (
                                        <option key={c.id} value={c.id} className="bg-[#1a1a3e]">
                                            {c.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Fecha
                                </label>
                                <input
                                    type="date"
                                    value={formData.fecha}
                                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                                    required
                                />
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 text-gray-300 hover:bg-white/10 rounded-lg"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                                >
                                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                    Añadir
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Group Modal */}
            {editModal.isOpen && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-[#1a1a3e] border border-white/10 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b border-white/10 shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-white">Editar Grupo</h2>
                                <p className="text-purple-400 font-medium">{formatDate(editModal.date)}</p>
                            </div>
                            <button
                                onClick={() => setEditModal({ ...editModal, isOpen: false })}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            {/* Add New Section */}
                            <div className="bg-white/5 p-4 rounded-xl border border-white/10 mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Añadir Conductor al grupo
                                </label>
                                <div className="flex gap-2">
                                    <select
                                        value={editModal.newConductorId}
                                        onChange={(e) => setEditModal({ ...editModal, newConductorId: e.target.value })}
                                        className="flex-1 px-4 py-2 bg-black/20 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                                    >
                                        <option value="" className="bg-[#1a1a3e]">Seleccionar conductor...</option>
                                        {conductores
                                            .filter(c => !editModal.currentConductors.some(curr => curr.conductor_id === c.id))
                                            .map(c => (
                                                <option key={c.id} value={c.id} className="bg-[#1a1a3e]">
                                                    {c.nombre}
                                                </option>
                                            ))
                                        }
                                    </select>
                                    <button
                                        onClick={handleAddToEdit}
                                        disabled={!editModal.newConductorId}
                                        className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <Plus className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Current List */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">
                                    Conductores ({editModal.currentConductors.length})
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {editModal.currentConductors.map(entry => (
                                        <div
                                            key={entry.id}
                                            className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg group hover:border-purple-500/30 transition-all"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">
                                                    {entry.conductor_nombre.substring(0, 2).toUpperCase()}
                                                </div>
                                                <span className="text-white">{entry.conductor_nombre}</span>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveFromEdit(entry.id)}
                                                className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                                                title="Eliminar del grupo"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {editModal.currentConductors.length === 0 && (
                                    <div className="text-center py-8 text-gray-500 bg-white/5 rounded-xl border border-white/5 border-dashed">
                                        No hay conductores asignados a este día
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t border-white/10 bg-[#1a1a3e] shrink-0 flex justify-end">
                            <button
                                onClick={() => setEditModal({ ...editModal, isOpen: false })}
                                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg hover:from-purple-700 hover:to-cyan-700 transition-all font-medium"
                            >
                                Hecho
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Eliminar Día"
                message="¿Estás seguro de que deseas eliminar este día libre individual?"
                confirmText="Eliminar"
                variant="danger"
            />
        </div>
    );
};

export default AnexoLibranzas;
